import React, { useState, useEffect, useMemo } from 'react';
import { Home, Plus, Trash2, DollarSign, TrendingUp, TrendingDown, Edit2, Save, X, RefreshCw, ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import useLocalStorage from './hooks/useLocalStorage';

// ── Constants ─────────────────────────────────────────────────

const getDefaultBudgets = () => [
  { id: 1, category: 'food',          name: 'Food & Dining',    limit: 300, color: 'bg-orange-500' },
  { id: 2, category: 'transport',     name: 'Transportation',   limit: 100, color: 'bg-blue-500' },
  { id: 3, category: 'books',         name: 'Books & Supplies', limit: 150, color: 'bg-purple-500' },
  { id: 4, category: 'entertainment', name: 'Entertainment',    limit: 100, color: 'bg-pink-500' },
  { id: 5, category: 'rent',          name: 'Rent & Utilities', limit: 800, color: 'bg-red-500' },
  { id: 6, category: 'other',         name: 'Other',            limit: 100, color: 'bg-neutral-500' },
];

const INCOME_RECURRENCE = ['none', 'weekly', 'biweekly', 'monthly', 'yearly'];
const RECURRENCE_LABELS = { none: 'One-time', weekly: 'Weekly', biweekly: 'Bi-weekly', monthly: 'Monthly', yearly: 'Yearly' };
const BILLING_CYCLES = ['monthly', 'yearly', 'weekly'];
const MAX_AMOUNT = 999_999;
const MAX_TEXT_LENGTH = 100;

// ── Validation ────────────────────────────────────────────────

const sanitize = (str) =>
  String(str ?? '').replace(/[<>"'`]/g, '').slice(0, MAX_TEXT_LENGTH);

const validateAmount = (val) => {
  const n = parseFloat(val);
  if (isNaN(n) || n <= 0) return 'Amount must be greater than 0';
  if (n > MAX_AMOUNT) return `Amount cannot exceed $${MAX_AMOUNT.toLocaleString()}`;
  return null;
};

// ── Date helpers ──────────────────────────────────────────────

const startOfWeek  = (d) => { const x = new Date(d); x.setHours(0,0,0,0); x.setDate(x.getDate() - x.getDay()); return x; };
const endOfWeek    = (d) => { const x = startOfWeek(d); x.setDate(x.getDate() + 6); x.setHours(23,59,59,999); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);
const startOfYear  = (d) => new Date(d.getFullYear(), 0, 1);
const endOfYear    = (d) => new Date(d.getFullYear(), 11, 31, 23, 59, 59, 999);

const periodRange = (period, anchor) => {
  if (period === 'week')  return [startOfWeek(anchor),  endOfWeek(anchor)];
  if (period === 'month') return [startOfMonth(anchor), endOfMonth(anchor)];
  if (period === 'year')  return [startOfYear(anchor),  endOfYear(anchor)];
  return [new Date(0), new Date(9999, 0)];
};

const fmtPeriodLabel = (period, anchor) => {
  if (period === 'week') {
    const s = startOfWeek(anchor), e = endOfWeek(anchor);
    return `${s.toLocaleDateString('en-US',{month:'short',day:'numeric'})} – ${e.toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}`;
  }
  if (period === 'month') return anchor.toLocaleDateString('en-US',{month:'long',year:'numeric'});
  if (period === 'year')  return String(anchor.getFullYear());
  return 'All Time';
};

const shiftAnchor = (period, anchor, dir) => {
  const d = new Date(anchor);
  if (period === 'week')  d.setDate(d.getDate() + dir * 7);
  if (period === 'month') d.setMonth(d.getMonth() + dir);
  if (period === 'year')  d.setFullYear(d.getFullYear() + dir);
  return d;
};

// ── Recurring expansion ───────────────────────────────────────

const expandRecurring = (transactions, from, to) => {
  if (!transactions) return [];
  const result = [];

  transactions.forEach(t => {
    if (t.type !== 'income' || !t.recurrence || t.recurrence === 'none') {
      const d = new Date(t.date);
      if (d >= from && d <= to) result.push(t);
      return;
    }
    let cur = new Date(t.date);
    let idx = 0;
    while (cur <= to) {
      if (cur >= from) result.push({ ...t, id: `${t.id}-exp-${idx}`, date: cur.toISOString().split('T')[0], _isExpanded: idx > 0 });
      if (t.recurrence === 'weekly')        cur = new Date(cur.getTime() + 7*86400000);
      else if (t.recurrence === 'biweekly') cur = new Date(cur.getTime() + 14*86400000);
      else if (t.recurrence === 'monthly')  { cur = new Date(cur); cur.setMonth(cur.getMonth()+1); }
      else if (t.recurrence === 'yearly')   { cur = new Date(cur); cur.setFullYear(cur.getFullYear()+1); }
      else break;
      if (++idx > 500) break;
    }
  });

  transactions.forEach(t => {
    if (t.type !== 'expense' || !t.recurring) return;
    const orig = new Date(t.date);
    let cur = new Date(orig);
    let idx = 0;
    while (cur <= to) {
      if (cur >= from && !(cur.getFullYear()===orig.getFullYear() && cur.getMonth()===orig.getMonth()))
        result.push({ ...t, id: `${t.id}-rexp-${idx}`, date: cur.toISOString().split('T')[0], _isExpanded: true });
      cur = new Date(cur); cur.setMonth(cur.getMonth()+1);
      if (++idx > 120) break;
    }
  });

  return result;
};

// ── Finance ───────────────────────────────────────────────────

const Finance = ({ setCurrentModule }) => {
  const [transactions,  setTransactions]  = useLocalStorage('transactions',  []);
  const [budgets,       setBudgets]       = useLocalStorage('budgets',       null);
  const [subscriptions, setSubscriptions] = useLocalStorage('subscriptions', []);

  const safeBudgets = budgets || getDefaultBudgets();
  useEffect(() => { if (!budgets) setBudgets(getDefaultBudgets()); }, []);

  const [activeTab, setActiveTab] = useState('overview');
  const [period,    setPeriod]    = useState('month');
  const [anchor,    setAnchor]    = useState(new Date());

  const [showTxModal,  setShowTxModal]  = useState(false);
  const [showBudModal, setShowBudModal] = useState(false);
  const [showSubModal, setShowSubModal] = useState(false);
  const [editTx,  setEditTx]  = useState(null);
  const [editBud, setEditBud] = useState(null);
  const [editSub, setEditSub] = useState(null);
  const [deleteTxConfirm, setDeleteTxConfirm] = useState(null);

  const monthlySubCost = (s) => {
    const a = parseFloat(s?.amount) || 0;
    if (s?.billingCycle === 'yearly') return a / 12;
    if (s?.billingCycle === 'weekly') return a * 4.33;
    return a;
  };

  const totalMonthlySubs = (subscriptions || []).reduce((s, x) => s + monthlySubCost(x), 0);

  const subCostForPeriod = (p) => {
    if (p === 'week')  return totalMonthlySubs / 4.33;
    if (p === 'month') return totalMonthlySubs;
    if (p === 'year')  return totalMonthlySubs * 12;
    const earliest = transactions.length ? new Date(Math.min(...transactions.map(t => new Date(t.date)))) : new Date();
    return totalMonthlySubs * Math.max(1, (new Date() - earliest) / (1000*60*60*24*30));
  };

  const stats = useMemo(() => {
    if (!transactions || !subscriptions) return { income:0, txExp:0, expenses:0, balance:0, byCategory:{}, txList:[] };
    const [from, to] = periodRange(period, anchor);
    const inRange = expandRecurring(transactions, from, to).filter(t => { const d = new Date(t.date); return d >= from && d <= to; });
    const income  = inRange.filter(t => t.type==='income').reduce((s,t) => s + parseFloat(t.amount), 0);
    const txExp   = inRange.filter(t => t.type==='expense').reduce((s,t) => s + parseFloat(t.amount), 0);
    const subsCost = subCostForPeriod(period);
    const byCategory = {};
    inRange.filter(t => t.type==='expense').forEach(t => { byCategory[t.category] = (byCategory[t.category]||0) + parseFloat(t.amount); });
    if (subsCost > 0) byCategory['subscriptions'] = (byCategory['subscriptions']||0) + subsCost;
    return { income, txExp, expenses: txExp + subsCost, balance: income - (txExp + subsCost), byCategory, txList: inRange.sort((a,b) => new Date(b.date)-new Date(a.date)) };
  }, [transactions, subscriptions, period, anchor]);

  const monthlyHistory = useMemo(() => {
    if (!transactions || !subscriptions) return [];
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - (11 - i));
      const [from, to] = periodRange('month', d);
      const inR = expandRecurring(transactions, from, to).filter(t => { const dt = new Date(t.date); return dt >= from && dt <= to; });
      return {
        label:  d.toLocaleDateString('en-US', { month: 'short' }),
        spent:  inR.filter(t=>t.type==='expense').reduce((s,t)=>s+parseFloat(t.amount),0) + totalMonthlySubs,
        income: inR.filter(t=>t.type==='income').reduce((s,t)=>s+parseFloat(t.amount),0),
      };
    });
  }, [transactions, subscriptions]);

  const getCategoryInfo = (cat) => {
    if (cat === 'subscriptions') return { name: 'Subscriptions', color: 'bg-teal-500' };
    return safeBudgets.find(b => b.category===cat) || { name: cat, color: 'bg-neutral-500' };
  };

  const periodBudget = () => {
    const base = safeBudgets.reduce((s,b) => s+b.limit, 0) + totalMonthlySubs;
    if (period==='week')  return base / 4.33;
    if (period==='month') return base;
    if (period==='year')  return base * 12;
    return Infinity;
  };

  const budgetForCatPeriod = (cat) => {
    const b = safeBudgets.find(x => x.category===cat);
    if (!b) return null;
    if (period==='week')  return b.limit / 4.33;
    if (period==='month') return b.limit;
    if (period==='year')  return b.limit * 12;
    return null;
  };

  const pBudget  = periodBudget();
  const usePct   = stats.income > 0
    ? Math.min((stats.expenses / stats.income) * 100, 100)
    : pBudget > 0 && isFinite(pBudget) ? Math.min((stats.expenses / pBudget) * 100, 100) : 0;
  const barColor = usePct >= 100 ? 'bg-red-500' : usePct >= 80 ? 'bg-orange-400' : 'bg-green-500';
  const maxBar   = Math.max(...monthlyHistory.map(m => Math.max(m.spent, m.income)), 1);

  const isCurrentPeriod = () => {
    const now = new Date();
    if (period==='week')  return startOfWeek(anchor).getTime() === startOfWeek(now).getTime();
    if (period==='month') return anchor.getMonth()===now.getMonth() && anchor.getFullYear()===now.getFullYear();
    if (period==='year')  return anchor.getFullYear()===now.getFullYear();
    return true;
  };

  const saveTx = () => {
    setTransactions(p => p.find(t => t.id===editTx.id) ? p.map(t => t.id===editTx.id ? editTx : t) : [editTx, ...p]);
    setShowTxModal(false); setEditTx(null);
  };
  const saveBud = () => {
    setBudgets(p => (p||getDefaultBudgets()).map(b => b.id===editBud.id ? editBud : b));
    setShowBudModal(false); setEditBud(null);
  };
  const saveSub = () => {
    setSubscriptions(p => p.find(s => s.id===editSub.id) ? p.map(s => s.id===editSub.id ? editSub : s) : [...p, editSub]);
    setShowSubModal(false); setEditSub(null);
  };
  const deleteTx = (id) => { setTransactions(p => p.filter(x => x.id !== id)); setDeleteTxConfirm(null); };

  const subDaysUntil = (sub) => {
    if (!sub.nextRenewal) return null;
    const t = new Date(); t.setHours(0,0,0,0);
    const r = new Date(sub.nextRenewal); r.setHours(0,0,0,0);
    return Math.ceil((r - t) / 86400000);
  };

  return (
    <div className="page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 md:p-8">

        <button onClick={() => setCurrentModule('dashboard')} className="btn-back">
          <Home className="w-4 h-4" /> Dashboard
        </button>

        {/* Header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <h1 className="page-title">FINANCE</h1>
            <p className="text-neutral-500 text-sm font-mono uppercase tracking-wide mt-1">Personal budget tracker</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => { setEditTx({ id: Date.now(), type:'income', amount:'', description:'', date: new Date().toISOString().split('T')[0], recurrence:'none' }); setShowTxModal(true); }}
              className="flex items-center gap-1.5 border-2 border-neutral-900 bg-white px-3 py-2 text-xs uppercase font-bold font-mono shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover-lift"
            >
              <TrendingUp className="w-4 h-4" /> Income
            </button>
            <button
              onClick={() => { setEditTx({ id: Date.now(), type:'expense', amount:'', category:'food', description:'', date: new Date().toISOString().split('T')[0], recurring: false }); setShowTxModal(true); }}
              className="flex items-center gap-1.5 bg-neutral-900 text-white border-2 border-neutral-900 px-3 py-2 text-xs uppercase font-bold font-mono shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover-lift hover:bg-neutral-700"
            >
              <TrendingDown className="w-4 h-4" /> Expense
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-2 border-neutral-900 bg-white mb-6 card-sm overflow-hidden">
          {[['overview','Overview'], ['subscriptions',`Subscriptions (${subscriptions.length})`], ['transactions','Transactions']].map(([id,label],i) => (
            <button key={id} onClick={() => setActiveTab(id)}
              className={`flex-1 px-3 py-3 text-xs font-mono font-bold uppercase tracking-wider transition-all ${i<2?'border-r-2 border-neutral-900':''} ${activeTab===id?'tab-active':'tab-inactive'}`}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <div className="space-y-6">

            {/* Period selector */}
            <div className="flex border-2 border-neutral-900 bg-white overflow-hidden card-sm">
              {[['week','Week'],['month','Month'],['year','Year'],['all','All Time']].map(([p,label],i) => (
                <button key={p} onClick={() => { setPeriod(p); setAnchor(new Date()); }}
                  className={`flex-1 py-2.5 text-xs font-mono font-bold uppercase tracking-wider transition-all ${i<3?'border-r-2 border-neutral-900':''} ${period===p?'tab-active':'tab-inactive'}`}>
                  {label}
                </button>
              ))}
            </div>

            {period !== 'all' && (
              <div className="flex items-center justify-between border-2 border-neutral-900 bg-white px-4 py-3 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
                <button onClick={() => setAnchor(shiftAnchor(period,anchor,-1))} className="p-1 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"><ChevronLeft className="w-4 h-4"/></button>
                <div className="text-center">
                  <div className="text-base font-serif text-neutral-900 uppercase">{fmtPeriodLabel(period,anchor)}</div>
                  {!isCurrentPeriod() && <button onClick={() => setAnchor(new Date())} className="text-xs font-mono text-neutral-400 hover:text-neutral-900 underline">Back to current</button>}
                </div>
                <button onClick={() => setAnchor(shiftAnchor(period,anchor,1))} className="p-1 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"><ChevronRight className="w-4 h-4"/></button>
              </div>
            )}

            {/* Summary card */}
            <div className={`border-4 border-neutral-900 p-6 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] ${stats.balance<0?'bg-red-50':'bg-green-50'}`}>
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="text-xs font-mono font-bold uppercase tracking-wider text-neutral-500 mb-1">
                    Spent {period==='all'?'overall':period==='week'?'this week':period==='month'?'this month':'this year'}
                  </div>
                  <div className={`text-5xl sm:text-6xl font-serif font-bold leading-none ${stats.balance<0?'text-red-600':'text-neutral-900'}`}>${stats.expenses.toFixed(0)}</div>
                  {stats.income>0 && <div className="text-sm font-mono text-neutral-500 mt-1.5">of ${stats.income.toFixed(0)} income</div>}
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono font-bold uppercase tracking-wider text-neutral-500 mb-1">Left</div>
                  <div className={`text-3xl font-serif font-bold ${stats.balance<0?'text-red-600':'text-green-600'}`}>{stats.balance<0?'-':'+'}${Math.abs(stats.balance).toFixed(0)}</div>
                </div>
              </div>

              {(stats.income > 0 || (isFinite(pBudget) && pBudget > 0)) && (
                <div className="mb-5">
                  <div className="bg-white border-2 border-neutral-900 h-4 overflow-hidden mb-1">
                    <div className={`h-full transition-all duration-700 ${barColor}`} style={{width:`${usePct}%`}} />
                  </div>
                  <div className="text-xs font-mono text-neutral-500">
                    {stats.income>0 ? `${usePct.toFixed(0)}% of income` : `${usePct.toFixed(0)}% of budget`}
                    {stats.income>0 && stats.expenses>stats.income && <span className="text-red-600 font-bold ml-2">⚠ over income</span>}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 pt-4 border-t-2 border-neutral-300">
                {[
                  { label: 'Transactions',  value: `$${stats.txExp.toFixed(0)}`,                   cls: 'text-neutral-900' },
                  { label: 'Subscriptions', value: `$${subCostForPeriod(period).toFixed(0)}`,       cls: 'text-teal-600' },
                  { label: 'Income',        value: `$${stats.income.toFixed(0)}`,                   cls: 'text-green-600' },
                ].map(({ label, value, cls }) => (
                  <div key={label} className="text-center">
                    <div className={`text-lg sm:text-xl font-bold font-mono ${cls}`}>{value}</div>
                    <div className="text-xs font-mono text-neutral-500 uppercase">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Category breakdown */}
            {Object.keys(stats.byCategory).length > 0 && (
              <div className="card-md bg-white">
                <div className="card-header-dark flex items-center justify-between">
                  <h3 className="text-base font-serif text-white uppercase tracking-wide">Spending by Category</h3>
                  <span className="text-xs font-mono text-neutral-400">{fmtPeriodLabel(period,anchor)}</span>
                </div>
                <div className="p-4 space-y-4">
                  {Object.entries(stats.byCategory).sort(([,a],[,b])=>b-a).map(([cat,amount]) => {
                    const info   = getCategoryInfo(cat);
                    const pct    = stats.expenses>0 ? (amount/stats.expenses)*100 : 0;
                    const catBud = budgetForCatPeriod(cat);
                    const isOver = catBud && amount > catBud;
                    return (
                      <div key={cat}>
                        <div className="flex items-center justify-between mb-1.5">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 border border-neutral-900 flex-shrink-0 ${info.color}`} />
                            <span className="text-sm font-mono font-bold uppercase">{info.name}</span>
                            {isOver && <span className="text-xs font-mono text-red-500 font-bold">over limit</span>}
                            {cat==='subscriptions' && <button onClick={() => setActiveTab('subscriptions')} className="text-xs font-mono text-teal-600 underline">manage</button>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className={`text-sm font-mono font-bold ${isOver?'text-red-600':''}`}>
                              ${amount.toFixed(2)}{catBud && <span className="font-normal text-neutral-400"> / ${catBud.toFixed(0)}</span>}
                            </span>
                            <span className="text-xs font-mono text-neutral-400 w-8 text-right">{pct.toFixed(0)}%</span>
                            {safeBudgets.find(b=>b.category===cat) && (
                              <button onClick={() => { setEditBud(safeBudgets.find(b=>b.category===cat)); setShowBudModal(true); }} className="border border-neutral-300 p-0.5 hover:border-neutral-900 transition-colors">
                                <Edit2 className="w-3 h-3"/>
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="bg-neutral-200 h-2.5 overflow-hidden">
                          <div className={`h-full transition-all duration-500 ${isOver?'bg-red-500':info.color}`} style={{width:`${pct}%`}} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* 12-month chart */}
            <div className="card-md bg-white">
              <div className="card-header-dark flex items-center justify-between">
                <h3 className="text-base font-serif text-white uppercase">12-Month Spending Pattern</h3>
                <div className="flex items-center gap-4 text-xs font-mono">
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-neutral-900 inline-block border border-white"/>Spent</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 inline-block border border-white"/>Income</span>
                </div>
              </div>
              <div className="p-4">
                <div className="flex items-end gap-1 h-32">
                  {monthlyHistory.map((m,i) => (
                    <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                      <div className="w-full flex items-end gap-0.5" style={{height:'96px'}}>
                        <div className="flex-1 bg-neutral-900 transition-all" style={{height:`${(m.spent/maxBar)*100}%`,  minHeight: m.spent>0?'2px':'0'}}/>
                        <div className="flex-1 bg-green-400 border border-neutral-900 transition-all" style={{height:`${(m.income/maxBar)*100}%`, minHeight: m.income>0?'2px':'0'}}/>
                      </div>
                      <div className="text-xs font-mono text-neutral-500 text-center">{m.label}</div>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between mt-2 border-t border-neutral-200 pt-2">
                  <span className="text-xs font-mono text-neutral-400">$0</span>
                  <span className="text-xs font-mono text-neutral-400">${(maxBar/2).toFixed(0)}</span>
                  <span className="text-xs font-mono text-neutral-400">${maxBar.toFixed(0)}</span>
                </div>
              </div>
            </div>

            {/* Recent */}
            {stats.txList.length > 0 && (
              <div className="card-sm bg-white">
                <div className="card-header flex items-center justify-between">
                  <h3 className="font-serif text-neutral-900 uppercase">Recent</h3>
                  <button onClick={() => setActiveTab('transactions')} className="text-xs font-mono font-bold border-2 border-neutral-900 px-2 py-1 hover:bg-neutral-900 hover:text-white transition-colors uppercase">All</button>
                </div>
                <div className="divide-y divide-neutral-100">
                  {stats.txList.slice(0,5).map(t => {
                    const info = getCategoryInfo(t.category||'other');
                    return (
                      <div key={t.id} className="list-row-subtle">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 border-2 border-neutral-900 flex items-center justify-center flex-shrink-0 ${t.type==='income'?'bg-green-400':info.color}`}>
                            {t.type==='income'?<TrendingUp className="w-4 h-4 text-neutral-900"/>:<TrendingDown className="w-4 h-4 text-white"/>}
                          </div>
                          <div>
                            <div className="text-sm font-mono font-bold uppercase">{sanitize(t.description)||info.name}</div>
                            <div className="text-xs font-mono text-neutral-400">{new Date(t.date).toLocaleDateString('en-US',{month:'short',day:'numeric'})}{t._isExpanded&&' · recurring'}</div>
                          </div>
                        </div>
                        <span className={`text-sm font-bold font-mono ${t.type==='income'?'text-green-600':'text-red-600'}`}>{t.type==='income'?'+':'-'}${parseFloat(t.amount).toFixed(2)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Subscriptions ── */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-4">
            <div className="card-md bg-white p-5 flex items-center justify-between">
              <div>
                <div className="text-3xl font-serif font-bold">${totalMonthlySubs.toFixed(2)}<span className="text-base font-normal text-neutral-400 font-mono">/mo</span></div>
                <div className="text-sm font-mono text-neutral-500">${(totalMonthlySubs*12).toFixed(2)}/yr · {subscriptions.length} sub{subscriptions.length!==1?'s':''}</div>
              </div>
              <button
                onClick={() => { setEditSub({ id: Date.now(), name:'', amount:'', billingCycle:'monthly', nextRenewal:'', notes:'' }); setShowSubModal(true); }}
                className="flex items-center gap-2 bg-neutral-900 text-white px-4 py-2 text-xs uppercase font-bold font-mono card-sm hover-lift-lg hover:bg-neutral-700"
              >
                <Plus className="w-4 h-4"/> Add
              </button>
            </div>

            {subscriptions.length === 0 ? (
              <div className="card-empty bg-white p-12 text-center shadow-[6px_6px_0px_0px_rgba(0,0,0,1)]">
                <RefreshCw className="w-12 h-12 text-neutral-400 mx-auto mb-4 border-4 border-neutral-900 p-2"/>
                <h3 className="text-xl font-serif mb-2 uppercase">No subscriptions yet</h3>
                <p className="text-sm font-mono text-neutral-500">Track Netflix, Spotify, gym memberships and more</p>
              </div>
            ) : (
              <div className="space-y-3">
                {subscriptions.slice().sort((a,b) => (subDaysUntil(a)??9999)-(subDaysUntil(b)??9999)).map(sub => {
                  const days = subDaysUntil(sub), monthly = monthlySubCost(sub);
                  const isUrgent = days!==null && days<=7 && days>=0;
                  const isOverdue = days!==null && days<0;
                  return (
                    <div key={sub.id} className={`border-2 bg-white shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover-lift-lg ${isOverdue?'border-red-600':isUrgent?'border-orange-400':'border-neutral-900'}`}>
                      <div className="p-4 flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-serif text-neutral-900 uppercase">{sanitize(sub.name)||'Unnamed'}</span>
                            <span className="text-xs font-mono border border-neutral-300 px-1.5 py-0.5 uppercase text-neutral-500">{sub.billingCycle}</span>
                            {isOverdue && <span className="text-xs font-mono border border-red-400 bg-red-50 text-red-600 px-1.5 uppercase">Overdue</span>}
                            {isUrgent  && <span className="text-xs font-mono border border-orange-400 bg-orange-50 text-orange-600 px-1.5 uppercase">Soon</span>}
                          </div>
                          <div className="text-xs font-mono text-neutral-400">
                            {sub.nextRenewal ? `${days===0?'Today':days===1?'Tomorrow':days<0?`${Math.abs(days)}d overdue`:`${days}d`} · ${new Date(sub.nextRenewal).toLocaleDateString('en-US',{month:'short',day:'numeric'})}` : 'No renewal date'}
                            {sub.notes && ` · ${sanitize(sub.notes)}`}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="text-right">
                            <div className="text-xl font-bold font-mono">${parseFloat(sub.amount||0).toFixed(2)}<span className="text-xs font-normal text-neutral-400">/{sub.billingCycle==='yearly'?'yr':sub.billingCycle==='weekly'?'wk':'mo'}</span></div>
                            {sub.billingCycle!=='monthly' && <div className="text-xs font-mono text-neutral-400">${monthly.toFixed(2)}/mo</div>}
                          </div>
                          <div className="flex gap-1">
                            <button onClick={() => { setEditSub(sub); setShowSubModal(true); }} className="btn-icon"><Edit2 className="w-3.5 h-3.5"/></button>
                            <button onClick={() => setSubscriptions(s => s.filter(x => x.id!==sub.id))} className="btn-icon-danger"><Trash2 className="w-3.5 h-3.5"/></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Transactions ── */}
        {activeTab === 'transactions' && (
          <div className="space-y-4">
            <div className="flex border-2 border-neutral-900 bg-white overflow-hidden shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]">
              {[['week','Week'],['month','Month'],['year','Year'],['all','All']].map(([p,label],i) => (
                <button key={p} onClick={() => { setPeriod(p); setAnchor(new Date()); }}
                  className={`flex-1 py-2 text-xs font-mono font-bold uppercase ${i<3?'border-r-2 border-neutral-900':''} ${period===p?'tab-active':'tab-inactive'}`}>{label}</button>
              ))}
            </div>

            {period !== 'all' && (
              <div className="flex items-center justify-between border-2 border-neutral-900 bg-white px-4 py-2">
                <button onClick={() => setAnchor(shiftAnchor(period,anchor,-1))} className="p-1 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"><ChevronLeft className="w-4 h-4"/></button>
                <span className="text-sm font-serif uppercase">{fmtPeriodLabel(period,anchor)}</span>
                <button onClick={() => setAnchor(shiftAnchor(period,anchor,1))} className="p-1 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white transition-all"><ChevronRight className="w-4 h-4"/></button>
              </div>
            )}

            <div className="card-md bg-white">
              {stats.txList.length === 0 ? (
                <div className="p-12 text-center"><p className="text-neutral-500 font-mono text-sm uppercase">No transactions for this period</p></div>
              ) : (
                <div className="divide-y divide-neutral-100">
                  {stats.txList.map(t => {
                    const info = getCategoryInfo(t.category||'other');
                    return (
                      <div key={t.id} className="flex items-center justify-between px-4 py-3 hover:bg-neutral-50 transition-colors group">
                        <div className="flex items-center gap-3">
                          <div className={`w-9 h-9 border-2 border-neutral-900 flex items-center justify-center flex-shrink-0 ${t.type==='income'?'bg-green-400':info.color}`}>
                            {t.type==='income'?<TrendingUp className="w-4 h-4 text-neutral-900"/>:<TrendingDown className="w-4 h-4 text-white"/>}
                          </div>
                          <div>
                            <div className="text-sm font-mono font-bold uppercase">{sanitize(t.description)||info.name}</div>
                            <div className="text-xs font-mono text-neutral-400 flex items-center gap-2">
                              {new Date(t.date).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}
                              {t.recurrence && t.recurrence!=='none' && !t._isExpanded && <span className="bg-blue-100 text-blue-700 border border-blue-300 px-1.5 uppercase text-xs">{RECURRENCE_LABELS[t.recurrence]}</span>}
                              {t.recurring && !t._isExpanded && !t.recurrence && <span className="bg-blue-100 text-blue-700 border border-blue-300 px-1.5 uppercase text-xs">Recurring</span>}
                              {t._isExpanded && <span className="text-neutral-400">· auto</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className={`text-base font-bold font-mono ${t.type==='income'?'text-green-600':'text-red-600'}`}>{t.type==='income'?'+':'-'}${parseFloat(t.amount).toFixed(2)}</span>
                          {!t._isExpanded && (
                            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button onClick={() => { setEditTx(t); setShowTxModal(true); }} className="btn-icon"><Edit2 className="w-3.5 h-3.5"/></button>
                              <button onClick={() => setDeleteTxConfirm(t.id)} className="btn-icon-danger"><Trash2 className="w-3.5 h-3.5"/></button>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Delete confirmation ── */}
      {deleteTxConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-white border-4 border-neutral-900 max-w-sm w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] p-6">
            <h3 className="text-xl font-serif text-neutral-900 uppercase mb-2">Delete transaction?</h3>
            <p className="text-sm font-mono text-neutral-600 mb-6">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTxConfirm(null)} className="flex-1 border-2 border-neutral-900 py-3 text-sm uppercase font-bold font-mono hover-lift">Cancel</button>
              <button onClick={() => deleteTx(deleteTxConfirm)} className="flex-1 bg-red-600 text-white py-3 text-sm uppercase font-bold font-mono border-2 border-neutral-900 hover:bg-red-700 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showTxModal  && editTx  && <TransactionModal  tx={editTx}   setTx={setEditTx}   budgets={safeBudgets} onSave={saveTx}  onCancel={() => { setShowTxModal(false);  setEditTx(null);  }}/>}
      {showBudModal && editBud && <BudgetModal        bud={editBud} setBud={setEditBud}                       onSave={saveBud} onCancel={() => { setShowBudModal(false); setEditBud(null); }}/>}
      {showSubModal && editSub && <SubscriptionModal  sub={editSub} setSub={setEditSub}                       onSave={saveSub} onCancel={() => { setShowSubModal(false); setEditSub(null); }}/>}
    </div>
  );
};

// ── Transaction Modal ─────────────────────────────────────────

const TransactionModal = ({ tx, setTx, budgets, onSave, onCancel }) => {
  const [error, setError] = useState(null);
  const isIncome = tx.type === 'income';

  const handleSave = () => {
    const amountErr = validateAmount(tx.amount);
    if (amountErr) { setError(amountErr); return; }
    if (!tx.date)  { setError('Date is required'); return; }
    setError(null);
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white border-4 border-neutral-900 max-w-md w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto">
        <div className="border-b-4 border-neutral-900 p-5 flex items-center justify-between bg-neutral-900">
          <h2 className="modal-title">{isIncome ? 'Add Income' : 'Add Expense'}</h2>
          <button onClick={onCancel} className="text-white border-2 border-white p-1 hover:bg-neutral-700"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-50 border-2 border-red-600 p-3 text-red-700 text-sm font-mono">
              <AlertCircle className="w-4 h-4 flex-shrink-0"/> {error}
            </div>
          )}

          <div>
            <label className="label mb-2">Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 font-mono">$</span>
              <input type="number" step="0.01" min="0.01" max={MAX_AMOUNT} value={tx.amount}
                onChange={e => { setError(null); setTx({...tx, amount: e.target.value}); }}
                placeholder="0.00" className="w-full border-4 border-neutral-900 pl-8 pr-3 py-3 focus:outline-none font-mono font-bold text-lg" autoFocus/>
            </div>
          </div>

          {!isIncome && (
            <div>
              <label className="label mb-2">Category *</label>
              <select value={tx.category} onChange={e => setTx({...tx, category: e.target.value})} className="w-full border-4 border-neutral-900 px-3 py-3 font-mono font-bold bg-white focus:outline-none">
                {budgets.map(b => <option key={b.id} value={b.category}>{b.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label mb-2">Description</label>
            <input type="text" value={tx.description} maxLength={MAX_TEXT_LENGTH}
              onChange={e => setTx({...tx, description: sanitize(e.target.value)})}
              placeholder={isIncome ? 'e.g., Stipend, Part-time job' : 'e.g., Lunch, Textbook'}
              className="w-full border-4 border-neutral-900 px-3 py-3 focus:outline-none font-mono"/>
          </div>

          <div>
            <label className="label mb-2">Date *</label>
            <input type="date" value={tx.date} onChange={e => { setError(null); setTx({...tx, date: e.target.value}); }}
              className="w-full border-4 border-neutral-900 px-3 py-3 focus:outline-none font-mono font-bold"/>
          </div>

          {isIncome ? (
            <div>
              <label className="label mb-2">Repeats</label>
              <div className="grid grid-cols-3 gap-2">
                {INCOME_RECURRENCE.map(r => (
                  <label key={r} className={`flex items-center gap-2 cursor-pointer border-2 px-3 py-2 transition-all ${tx.recurrence===r?'border-neutral-900 bg-neutral-900 text-white':'border-neutral-300 hover:border-neutral-900'}`}>
                    <input type="radio" name="recurrence" value={r} checked={tx.recurrence===r} onChange={e => setTx({...tx, recurrence: e.target.value})} className="sr-only"/>
                    <span className="text-xs font-mono font-bold uppercase">{RECURRENCE_LABELS[r]}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <label className="flex items-center gap-3 cursor-pointer border-2 border-neutral-900 p-3">
              <div className="relative">
                <input type="checkbox" checked={tx.recurring||false} onChange={e => setTx({...tx, recurring: e.target.checked})} className="w-5 h-5 border-2 border-neutral-900 appearance-none checked:bg-neutral-900 cursor-pointer"/>
                {tx.recurring && <svg className="w-5 h-5 text-white absolute inset-0 pointer-events-none" viewBox="0 0 16 16" fill="none"><path d="M3 8L6 11L13 4" stroke="currentColor" strokeWidth="3"/></svg>}
              </div>
              <span className="text-sm font-mono font-bold uppercase">Recurring monthly</span>
            </label>
          )}
        </div>

        <div className="border-t-4 border-neutral-900 p-5 flex gap-3">
          <button onClick={onCancel} className="flex-1 border-2 border-neutral-900 py-3 text-sm uppercase font-bold font-mono hover-lift">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-neutral-900 text-white py-3 text-sm uppercase font-bold font-mono hover:bg-neutral-700 flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
            <Save className="w-4 h-4"/> Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Budget Modal ──────────────────────────────────────────────

const BudgetModal = ({ bud, setBud, onSave, onCancel }) => {
  const [error, setError] = useState(null);

  const handleSave = () => {
    const err = validateAmount(bud.limit);
    if (err) { setError(err); return; }
    setError(null);
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white border-4 border-neutral-900 max-w-md w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)]">
        <div className="border-b-4 border-neutral-900 p-5 flex items-center justify-between bg-neutral-900">
          <h2 className="modal-title">Edit Budget Limit</h2>
          <button onClick={onCancel} className="text-white border-2 border-white p-1 hover:bg-neutral-700"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 border-2 border-red-600 p-3 text-red-700 text-sm font-mono"><AlertCircle className="w-4 h-4"/>{error}</div>}
          <div>
            <label className="label mb-2">Category</label>
            <input value={bud.name} disabled className="w-full border-4 border-neutral-900 px-3 py-3 bg-neutral-100 cursor-not-allowed font-mono font-bold"/>
          </div>
          <div>
            <label className="label mb-2">Monthly Limit *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 font-mono">$</span>
              <input type="number" step="0.01" min="0.01" max={MAX_AMOUNT} value={bud.limit}
                onChange={e => { setError(null); setBud({...bud, limit: parseFloat(e.target.value)||0}); }}
                className="w-full border-4 border-neutral-900 pl-8 pr-3 py-3 focus:outline-none font-mono font-bold text-lg" autoFocus/>
            </div>
          </div>
        </div>
        <div className="border-t-4 border-neutral-900 p-5 flex gap-3">
          <button onClick={onCancel} className="flex-1 border-2 border-neutral-900 py-3 text-sm uppercase font-bold font-mono hover-lift">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-neutral-900 text-white py-3 text-sm uppercase font-bold font-mono hover:bg-neutral-700 flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"><Save className="w-4 h-4"/> Save</button>
        </div>
      </div>
    </div>
  );
};

// ── Subscription Modal ────────────────────────────────────────

const SubscriptionModal = ({ sub, setSub, onSave, onCancel }) => {
  const [error, setError] = useState(null);

  const handleSave = () => {
    if (!sanitize(sub.name).trim()) { setError('Name is required'); return; }
    const amountErr = validateAmount(sub.amount);
    if (amountErr) { setError(amountErr); return; }
    setError(null);
    onSave();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="bg-white border-4 border-neutral-900 max-w-md w-full shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] max-h-[90vh] overflow-y-auto">
        <div className="border-b-4 border-neutral-900 p-5 flex items-center justify-between bg-neutral-900">
          <h2 className="modal-title">{sub.name?'Edit':'New'} Subscription</h2>
          <button onClick={onCancel} className="text-white border-2 border-white p-1 hover:bg-neutral-700"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          {error && <div className="flex items-center gap-2 bg-red-50 border-2 border-red-600 p-3 text-red-700 text-sm font-mono"><AlertCircle className="w-4 h-4"/>{error}</div>}

          <div>
            <label className="label mb-2">Name *</label>
            <input type="text" value={sub.name} maxLength={MAX_TEXT_LENGTH}
              onChange={e => { setError(null); setSub({...sub, name: sanitize(e.target.value)}); }}
              placeholder="e.g., Netflix, Spotify, Gym"
              className="w-full border-4 border-neutral-900 px-3 py-3 focus:outline-none font-mono font-bold" autoFocus/>
          </div>

          <div>
            <label className="label mb-2">Amount *</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 font-mono">$</span>
              <input type="number" step="0.01" min="0.01" max={MAX_AMOUNT} value={sub.amount}
                onChange={e => { setError(null); setSub({...sub, amount: e.target.value}); }}
                placeholder="0.00" className="w-full border-4 border-neutral-900 pl-8 pr-3 py-3 focus:outline-none font-mono font-bold text-lg"/>
            </div>
          </div>

          <div>
            <label className="label mb-2">Billing Cycle</label>
            <div className="flex gap-3">
              {BILLING_CYCLES.map(c => (
                <label key={c} className={`flex-1 flex items-center justify-center cursor-pointer border-2 px-3 py-2 transition-all ${sub.billingCycle===c?'border-neutral-900 bg-neutral-900 text-white':'border-neutral-300 hover:border-neutral-900'}`}>
                  <input type="radio" name="billing" value={c} checked={sub.billingCycle===c} onChange={e => setSub({...sub, billingCycle: e.target.value})} className="sr-only"/>
                  <span className="text-xs font-mono font-bold uppercase">{c}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="label mb-2">Next Renewal</label>
            <input type="date" value={sub.nextRenewal} onChange={e => setSub({...sub, nextRenewal: e.target.value})}
              className="w-full border-4 border-neutral-900 px-3 py-3 focus:outline-none font-mono font-bold"/>
          </div>

          <div>
            <label className="label mb-2">Notes</label>
            <input type="text" value={sub.notes} maxLength={MAX_TEXT_LENGTH}
              onChange={e => setSub({...sub, notes: sanitize(e.target.value)})}
              placeholder="e.g., Student discount, family plan"
              className="w-full border-4 border-neutral-900 px-3 py-3 focus:outline-none font-mono"/>
          </div>
        </div>
        <div className="border-t-4 border-neutral-900 p-5 flex gap-3">
          <button onClick={onCancel} className="flex-1 border-2 border-neutral-900 py-3 text-sm uppercase font-bold font-mono hover-lift">Cancel</button>
          <button onClick={handleSave} className="flex-1 bg-neutral-900 text-white py-3 text-sm uppercase font-bold font-mono hover:bg-neutral-700 flex items-center justify-center gap-2 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]"><Save className="w-4 h-4"/> Save</button>
        </div>
      </div>
    </div>
  );
};

export default Finance;